import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthMe } from "@vorloop/api-client-react";
import { useCanvasAwareness, type SharedStroke } from "../../hooks/useCanvasAwareness";
import { IconUp } from "../layout/Icons";

/**
 * Replit-style Canvas — a frame editor wrapping the live app preview.
 * Sub-tabs at top toggle between "🎨 Canvas" (this frame view) and the
 * project's bare preview. The frame has a magenta selection border +
 * corner brackets; a floating action bar at the bottom holds the
 * mode-switcher (select / pan / Chat / Draw / Edit / + Generate) and a
 * "Request changes or chat about frame…" input.
 *
 * Y.js powers shared annotations (Y.Array<Stroke>) + peer cursors via
 * the canvas-broker. Strokes draw as an overlay above the iframe when
 * the Draw mode is active.
 */

interface Point {
  readonly x: number;
  readonly y: number;
}
interface DraftStroke {
  id: string;
  points: Point[];
  color: string;
  width: number;
  author: string;
  tool: "pen" | "eraser";
}

type CanvasMode = "select" | "pan" | "chat" | "draw" | "edit" | "generate";

const DRAW_COLOR = "#ec4899";
const DRAW_WIDTH = 3;
const PRESENCE_COLORS = ["#60a5fa", "#a78bfa", "#34d399", "#fbbf24", "#f87171", "#f472b6"];

function colorForUser(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return PRESENCE_COLORS[Math.abs(h) % PRESENCE_COLORS.length]!;
}

export function CanvasBoard({
  projectId,
  projectName,
  previewUrl,
}: {
  projectId: string;
  projectName: string;
  previewUrl: string;
}) {
  const [subtab, setSubtab] = useState<"canvas" | "project">("canvas");
  const [mode, setMode] = useState<CanvasMode>("select");
  const [composer, setComposer] = useState("");
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef<DraftStroke | null>(null);
  const [, repaintTick] = useState(0);

  const me = useAuthMe();
  const identity = useMemo(
    () => ({
      label: me.data?.user?.name ?? me.data?.user?.handle ?? "anon",
      color: colorForUser(me.data?.user?.id ?? "anon"),
    }),
    [me.data?.user?.id, me.data?.user?.name, me.data?.user?.handle],
  );
  const {
    peers,
    strokes,
    setLocalCursor,
    addStroke,
    state: presenceState,
  } = useCanvasAwareness(projectId, identity);

  // Repaint the overlay canvas whenever strokes / drawing change.
  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const s of strokes) {
      ctx.strokeStyle = s.tool === "eraser" ? "rgba(0,0,0,0)" : s.color;
      ctx.lineWidth = s.width;
      ctx.beginPath();
      s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    }
    const live = drawingRef.current;
    if (live) {
      ctx.strokeStyle = live.color;
      ctx.lineWidth = live.width;
      ctx.beginPath();
      live.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    }
  }, [strokes]);

  function relativeXY(e: React.MouseEvent | MouseEvent): Point {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
  }

  function onOverlayMouseDown(e: React.MouseEvent) {
    if (mode !== "draw") return;
    const p = relativeXY(e);
    drawingRef.current = {
      id: `me_${Date.now()}`,
      points: [p],
      color: DRAW_COLOR,
      width: DRAW_WIDTH,
      author: "me",
      tool: "pen",
    };
    repaintTick((n) => n + 1);
  }
  function onOverlayMouseMove(e: React.MouseEvent) {
    const p = relativeXY(e);
    setLocalCursor(p);
    const cur = drawingRef.current;
    if (!cur) return;
    cur.points.push(p);
    repaintTick((n) => n + 1);
  }
  function onOverlayMouseUp() {
    const cur = drawingRef.current;
    if (!cur) return;
    drawingRef.current = null;
    if (cur.points.length < 2) return;
    const finished: SharedStroke = {
      id: cur.id,
      points: cur.points.map((p) => ({ x: p.x, y: p.y })),
      color: cur.color,
      width: cur.width,
      author: cur.author,
      tool: cur.tool,
    };
    addStroke(finished);
    repaintTick((n) => n + 1);
  }
  function onOverlayMouseLeave() {
    setLocalCursor(null);
  }

  function onComposerSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!composer.trim()) return;
    // Hand the request to the chat dock. ChatDock listens for this
    // event and pre-fills its composer with the same string, then
    // submits the turn.
    window.dispatchEvent(
      new CustomEvent<{ prompt: string }>("vp:canvas:request-change", {
        detail: { prompt: composer.trim() },
      }),
    );
    setComposer("");
  }

  const drawingActive = mode === "draw";

  return (
    <div
      data-testid="canvas-board"
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        background: "var(--bg-canvas)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Sub-tab strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "8px 14px",
          borderBottom: "1px solid var(--border-default)",
          background: "var(--bg-panel)",
          flexShrink: 0,
        }}
      >
        <SubTab
          active={subtab === "canvas"}
          onClick={() => setSubtab("canvas")}
          icon="🎨"
          label="Canvas"
          testId="canvas-subtab-canvas"
        />
        <SubTab
          active={subtab === "project"}
          onClick={() => setSubtab("project")}
          icon="📱"
          label={projectName}
          testId="canvas-subtab-project"
        />
        <div style={{ flex: 1 }} />
        <PresenceBar identity={identity} peers={peers} state={presenceState} />
      </div>

      {/* Scene area — dotted background with the frame centered */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          background: "#0F1115",
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          overflow: "auto",
          padding: "32px 32px 96px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Decorative pink dots (Replit's "snap markers") */}
        {subtab === "canvas" && (
          <>
            <ScenePin top={70} left={32} />
            <ScenePin bottom={140} left={64} />
            <ScenePin top={120} right={48} />
            <ScenePin bottom={180} right={32} />
          </>
        )}

        {/* The "frame" — wraps the live preview. Magenta selection border +
            corner brackets when on the Canvas sub-tab, plain when on the
            project sub-tab. */}
        <div
          ref={frameRef}
          data-testid="canvas-frame"
          onMouseMove={(e) => {
            if (subtab !== "canvas") return;
            const rect = frameRef.current?.getBoundingClientRect();
            if (!rect) return;
            setLocalCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }}
          onMouseLeave={() => {
            if (subtab !== "canvas") return;
            setLocalCursor(null);
          }}
          style={{
            position: "relative",
            width: "min(420px, 100%)",
            height: "min(720px, 80vh)",
            background: "#0F1115",
            borderRadius: 22,
            overflow: "visible",
            boxShadow: subtab === "canvas"
              ? "0 0 0 2px #ec4899, 0 30px 80px -20px rgba(0,0,0,0.6)"
              : "0 30px 80px -20px rgba(0,0,0,0.6)",
          }}
        >
          {subtab === "canvas" && (
            <>
              <CornerBracket position="tl" />
              <CornerBracket position="tr" />
              <CornerBracket position="bl" />
              <CornerBracket position="br" />
              <EdgeHandle position="t" />
              <EdgeHandle position="r" />
              <EdgeHandle position="b" />
              <EdgeHandle position="l" />
            </>
          )}

          {/* The actual preview lives inside the frame. */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 22,
              overflow: "hidden",
              background: "#000",
            }}
          >
            <iframe
              src={previewUrl}
              title={`${projectName} preview`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#000",
                // Disable iframe pointer events while Draw / Edit / Generate
                // modes are active so the overlay receives the events.
                pointerEvents: drawingActive ? "none" : "auto",
              }}
            />
          </div>

          {/* Annotations overlay. On the Canvas sub-tab it always
              captures pointer events so the canvas (not the iframe) is
              the interaction surface — Replit's canvas blocks direct
              preview clicks; "Generate"/"Edit"/etc act on the frame
              itself. Cursor + drawing both happen here. */}
          {subtab === "canvas" && (
            <canvas
              ref={overlayRef}
              data-testid="canvas-draw-overlay"
              onMouseDown={onOverlayMouseDown}
              onMouseMove={onOverlayMouseMove}
              onMouseUp={onOverlayMouseUp}
              onMouseLeave={onOverlayMouseLeave}
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 22,
                pointerEvents: "auto",
                cursor: drawingActive ? "crosshair" : "default",
              }}
            />
          )}
        </div>

        {/* Peer cursors */}
        {peers.map((p) => (
          <div
            key={p.clientId}
            aria-hidden
            data-testid="canvas-peer-cursor"
            style={{
              position: "absolute",
              left: p.x,
              top: p.y,
              pointerEvents: "none",
              transition: "left 60ms linear, top 60ms linear",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2L14 7L8 9L7 15Z" fill={p.color} stroke="#0F1115" strokeWidth="0.5" />
            </svg>
            <span
              style={{
                position: "absolute",
                left: 14,
                top: 12,
                padding: "1px 6px",
                fontSize: 10,
                fontWeight: 600,
                color: "#fff",
                background: p.color,
                borderRadius: 4,
                whiteSpace: "nowrap",
              }}
            >
              {p.label}
            </span>
          </div>
        ))}

        {/* Floating bottom: chat composer + mode bar */}
        {subtab === "canvas" && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: 16,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              zIndex: 6,
              width: "min(580px, 90%)",
            }}
          >
            <form
              onSubmit={onComposerSubmit}
              data-testid="canvas-composer"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 8px 6px 14px",
                background: "rgba(15,17,21,0.92)",
                border: "1px solid var(--border-default)",
                borderRadius: 999,
                backdropFilter: "blur(6px)",
                boxShadow: "0 8px 30px -10px rgba(0,0,0,0.55)",
              }}
            >
              <input
                type="text"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                placeholder="Request changes or chat about frame…"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "var(--text-primary)",
                  fontSize: 13,
                }}
              />
              <button
                type="submit"
                disabled={!composer.trim()}
                aria-label="Send request"
                style={{
                  width: 28,
                  height: 28,
                  background: composer.trim() ? "var(--accent-blue)" : "var(--bg-active)",
                  color: composer.trim() ? "#fff" : "var(--text-tertiary)",
                  border: "none",
                  borderRadius: 999,
                  cursor: composer.trim() ? "pointer" : "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconUp size={12} />
              </button>
            </form>
            <ModeBar mode={mode} onChange={setMode} />
          </div>
        )}
      </div>
    </div>
  );
}

function SubTab({
  active,
  onClick,
  icon,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        background: active ? "var(--bg-canvas)" : "transparent",
        border: `1px solid ${active ? "var(--border-default)" : "transparent"}`,
        borderRadius: 7,
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: 12,
        cursor: "pointer",
        maxWidth: 240,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function ModeBar({
  mode,
  onChange,
}: {
  mode: CanvasMode;
  onChange: (m: CanvasMode) => void;
}) {
  const [generateOpen, setGenerateOpen] = useState(false);
  const buttons: ReadonlyArray<{ id: CanvasMode; label: string; icon: string; primary?: boolean }> = [
    { id: "select", label: "Select", icon: "▷" },
    { id: "pan", label: "Pan", icon: "✋" },
    { id: "chat", label: "Chat", icon: "💬" },
    { id: "draw", label: "Draw", icon: "✏️" },
    { id: "edit", label: "Edit", icon: "✎" },
    { id: "generate", label: "+ Generate", icon: "✨", primary: true },
  ];

  function pickGenerate(kind: string) {
    setGenerateOpen(false);
    // Route through the agent: prefill the chat with a generation
    // request that the user can edit before sending. The image and
    // component tools live in the agent's tool catalog, so this is the
    // path that actually mutates the project.
    const prompts: Record<string, string> = {
      Image: "Generate an image for this project and save it under assets/.",
      Component: "Generate a new React component for this project. Be specific about the props and structure.",
      Design: "Generate a design proposal — describe layout, components, and visual direction.",
    };
    const prompt = prompts[kind] ?? `Generate a ${kind} for this project.`;
    window.dispatchEvent(new CustomEvent("vp:chat:prefill", { detail: { prompt } }));
    window.dispatchEvent(new CustomEvent<{ kind: string }>("vp:canvas:generate", { detail: { kind } }));
  }

  return (
    <div style={{ position: "relative" }}>
      <div
        role="toolbar"
        aria-label="Canvas mode"
        data-testid="canvas-mode-bar"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          padding: 4,
          background: "rgba(15,17,21,0.92)",
          border: "1px solid var(--border-default)",
          borderRadius: 999,
          boxShadow: "0 10px 30px -10px rgba(0,0,0,0.55)",
        }}
      >
        {buttons.map((b) => {
          const active = mode === b.id;
          const isPrimary = b.primary;
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => {
                if (b.id === "generate") {
                  setGenerateOpen((v) => !v);
                  return;
                }
                onChange(b.id);
              }}
              data-testid={`canvas-mode-${b.id}`}
              aria-pressed={active}
              aria-expanded={b.id === "generate" ? generateOpen : undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: b.id === "select" || b.id === "pan" ? "6px 8px" : "6px 12px",
                background: isPrimary
                  ? "var(--accent-blue)"
                  : active
                  ? "var(--bg-active)"
                  : "transparent",
                color: isPrimary || active ? "var(--text-primary)" : "var(--text-secondary)",
                border: "none",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: isPrimary ? 600 : 500,
                cursor: "pointer",
              }}
            >
              <span aria-hidden>{b.icon}</span>
              {b.id !== "select" && b.id !== "pan" && <span>{b.label}</span>}
            </button>
          );
        })}
      </div>
      {generateOpen && (
        <div
          data-testid="canvas-generate-menu"
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 8px)",
            width: 260,
            background: "var(--bg-panel)",
            border: "1px solid var(--border-default)",
            borderRadius: 10,
            padding: 4,
            boxShadow: "0 18px 50px -10px rgba(0,0,0,0.55)",
          }}
        >
          {GENERATE_ITEMS.map((g) => (
            <button
              key={g.id}
              type="button"
              role="menuitem"
              onClick={() => pickGenerate(g.title)}
              data-testid={`canvas-generate-${g.id}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "8px 10px",
                width: "100%",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                cursor: "pointer",
                textAlign: "left",
                borderRadius: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-panel-2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span
                aria-hidden
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: "var(--bg-panel-2)",
                  border: "1px solid var(--border-default)",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {g.icon}
              </span>
              <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{g.title}</span>
                <span style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.4 }}>
                  {g.blurb}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const GENERATE_ITEMS = [
  { id: "design", title: "Design", icon: "▣", blurb: "Mock up interfaces and visual concepts" },
  { id: "image", title: "Image", icon: "🖼", blurb: "Generate and edit images" },
  { id: "video", title: "Video", icon: "▶", blurb: "Generate motion clips and animations" },
  { id: "vector", title: "Vector Graphic", icon: "✦", blurb: "Generate vector illustrations and SVGs" },
] as const;

function PresenceBar({
  identity,
  peers,
  state,
}: {
  identity: { label: string; color: string };
  peers: ReadonlyArray<{ clientId: number; label: string; color: string }>;
  state: string;
}) {
  return (
    <div
      data-testid="canvas-presence-bar"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 8px",
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border-default)",
        borderRadius: 999,
      }}
    >
      <Avatar label={identity.label} color={identity.color} />
      {peers.slice(0, 3).map((p) => (
        <Avatar key={p.clientId} label={p.label} color={p.color} />
      ))}
      <span
        aria-hidden
        title={`presence: ${state}`}
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: state === "open" ? "#34d399" : "#9ca3af",
          display: "inline-block",
          marginLeft: 4,
        }}
      />
    </div>
  );
}

function Avatar({ label, color }: { label: string; color: string }) {
  return (
    <span
      title={label}
      aria-label={label}
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        background: color,
        color: "#fff",
        fontSize: 9,
        fontWeight: 700,
        textTransform: "uppercase",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {label.charAt(0)}
    </span>
  );
}

function CornerBracket({ position }: { position: "tl" | "tr" | "bl" | "br" }) {
  const base: React.CSSProperties = {
    position: "absolute",
    width: 16,
    height: 16,
    border: "2px solid #ec4899",
    pointerEvents: "none",
  };
  const pos: Record<typeof position, React.CSSProperties> = {
    tl: { top: -8, left: -8, borderRight: "none", borderBottom: "none" },
    tr: { top: -8, right: -8, borderLeft: "none", borderBottom: "none" },
    bl: { bottom: -8, left: -8, borderRight: "none", borderTop: "none" },
    br: { bottom: -8, right: -8, borderLeft: "none", borderTop: "none" },
  };
  return <span aria-hidden style={{ ...base, ...pos[position] }} />;
}

function EdgeHandle({ position }: { position: "t" | "r" | "b" | "l" }) {
  const base: React.CSSProperties = {
    position: "absolute",
    width: 8,
    height: 8,
    background: "#ec4899",
    borderRadius: 999,
    pointerEvents: "none",
  };
  const pos: Record<typeof position, React.CSSProperties> = {
    t: { top: -4, left: "calc(50% - 4px)" },
    r: { top: "calc(50% - 4px)", right: -4 },
    b: { bottom: -4, left: "calc(50% - 4px)" },
    l: { top: "calc(50% - 4px)", left: -4 },
  };
  return <span aria-hidden style={{ ...base, ...pos[position] }} />;
}

function ScenePin({
  top,
  bottom,
  left,
  right,
}: {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}) {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        ...(top !== undefined && { top }),
        ...(bottom !== undefined && { bottom }),
        ...(left !== undefined && { left }),
        ...(right !== undefined && { right }),
        width: 5,
        height: 5,
        borderRadius: 999,
        background: "#ec4899",
        opacity: 0.7,
      }}
    />
  );
}
